import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../utils/constants';
import { getImageUrl } from '../utils/helpers';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';

const SubjectCategory = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [rawResponse, setRawResponse] = useState(null);

  // Get params from navigation state (matching mobile app)
  const { type, name, flag, courseId: stateCourseId } = location.state || {};
  
  // Use courseId from state if available, otherwise from URL
  const actualCourseId = stateCourseId || courseId;

  useEffect(() => {
    if (actualCourseId) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualCourseId]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      console.log('Fetching subject categories:', { actualCourseId, courseId, flag });
      
      // Mobile app uses /subjectbylevel/{courseId}
      const response = await axios.get(`${API_URL}/subjectbylevel/${actualCourseId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });
      
      console.log('SubjectCategory Full Response:', response);
      console.log('SubjectCategory Response Data:', response.data);
      console.log('SubjectCategory Response Data Type:', typeof response.data);
      console.log('SubjectCategory Response Is Array:', Array.isArray(response.data));
      
      // Save raw response for debugging
      setRawResponse(response.data);
      
      if (response.status === 200 && response.data) {
        // Handle both array and object with data property
        const categoriesData = Array.isArray(response.data) 
          ? response.data 
          : (response.data.data || response.data);
        
        console.log('Setting categories:', categoriesData);
        console.log('Categories length:', Array.isArray(categoriesData) ? categoriesData.length : 'not an array');
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to SubjectSubCategory (matching mobile app handleClick)
  const handleCategoryClick = useCallback((item) => {
    // Check if user has purchased (flag === 1 means NOT purchased)
    if (flag === 1) {
      toast.error('Please purchase this course to access the content');
      return;
    }
    
    navigate(`/subject-subcategory/${item.id}`, {
      state: {
        id: item.id,
        type: item.type,
        name: item.subname,
        flag,
      }
    });
  }, [navigate, flag]);

  if (loading) return <Loader />;

  // If not purchased (flag === 1), show purchase required message
  if (flag === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-8xl mb-6">🔒</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Purchase Required
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Please purchase this course to access the categories and content.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="container">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            {name || 'Subject Categories'}
          </h1>
          <p className="text-white/90">Select a category to continue</p>
          
          {/* Debug Info */}
        
        </div>
      </div>

      {/* Categories Grid */}
      <div className="py-8 sm:py-12 lg:py-16">
        <div className="container">
          {categories.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
              {categories.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleCategoryClick(item)}
                  className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group ${
                    flag === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {/* Category Image */}
                  {item.images && (
                    <div className="relative h-48 overflow-hidden bg-gray-200">
                      <img 
                        src={getImageUrl(item.images)}
                        alt={item.subname}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x200?text=Category';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Category Name */}
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-gray-800 text-center line-clamp-2">
                      {item.subname}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 sm:py-24">
              <div className="text-6xl sm:text-8xl mb-6">📂</div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-700 mb-3">
                No Categories Found
              </h3>
              <p className="text-gray-500 text-base sm:text-lg mb-8">
                No categories available for this subject.
              </p>
              
              {/* Debug: Show raw API response */}
              {rawResponse && (
                <div className="max-w-2xl mx-auto bg-gray-100 p-4 rounded text-left mb-8">
                  <h4 className="font-bold mb-2">API Response Debug:</h4>
                  <pre className="text-xs overflow-auto max-h-96">
                    {JSON.stringify(rawResponse, null, 2)}
                  </pre>
                </div>
              )}
              
              <button 
                onClick={() => navigate(-1)} 
                className="btn btn-primary btn-large"
              >
                Go Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectCategory;

